import React, { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Wordmark } from '../ui/Wordmark'
import { AvatarMenu } from '../ui/AvatarMenu'
import { useAuthStore } from '../../store/auth'
import { useNotifications } from '../../hooks/useNotifications'
import { useIsMobile } from '../../hooks/useIsMobile'
import { Bell, Menu, X } from 'lucide-react'

interface PublicLayoutProps {
  children: React.ReactNode
  activeNav?: 'Discover' | 'Churches' | 'Host an event'
  hideFooter?: boolean
}

export function PublicLayout({ children, activeNav, hideFooter }: PublicLayoutProps) {
  const { accessToken } = useAuthStore()
  const { data: notifData } = useNotifications()
  const unreadCount = accessToken ? (notifData?.data?.unreadCount ?? 0) : 0
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const navlinks = [
    { link: 'Discover', href: '/search' },
    { link: 'Churches', href: '/churches' },
    { link: 'Host an event', href: '/host' },
  ]

  function closeDrawer() { setDrawerOpen(false) }

  return (
    <div
      className={hideFooter ? 'page-shell-fullscreen' : undefined}
      style={{
        background: 'var(--bg)',
        color: 'var(--ink)',
        ...(hideFooter ? { overflow: 'hidden' } : { minHeight: '100vh' }),
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
      }}
    >
      {/* Nav */}
      <header
        style={{
          background: 'var(--bg)',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          borderBottom: '1px solid var(--line-soft)',
        }}
      >
        <div
          style={{
            margin: '0 auto',
            padding: isMobile ? '14px 16px' : '18px 36px',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 0 : 32,
          }}
        >
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <Wordmark size={22} />
          </Link>

          {/* Desktop nav links */}
          {!isMobile && (
            <nav style={{ display: 'flex', gap: 28, fontSize: 14.5, fontWeight: 500 }}>
              {navlinks.map((item) => {
                const isActive = activeNav === item.link
                return (
                  <NavLink
                    key={item.link}
                    to={item.href}
                    style={{
                      position: 'relative',
                      textDecoration: 'none',
                      color: isActive ? 'var(--ink)' : 'var(--ink-2)',
                    }}
                  >
                    {item.link}
                    {isActive && (
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: -6,
                          height: 3,
                          borderRadius: 999,
                          background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
                        }}
                      />
                    )}
                  </NavLink>
                )
              })}
            </nav>
          )}

          {/* Desktop right side */}
          {!isMobile && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              {accessToken ? (
                <>
                  <NavLink
                    to="/my-events"
                    style={{
                      textDecoration: 'none',
                      color: 'var(--ink-2)',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    My Events
                  </NavLink>
                  <Link
                    to="/profile"
                    aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                    style={{
                      position: 'relative',
                      color: 'var(--ink-2)',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <Bell size={20} strokeWidth={1.7} />
                    {unreadCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -4,
                          right: -5,
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
                          color: '#fff',
                          fontSize: 9,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <AvatarMenu size={34} />
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: 'none',
                      color: 'var(--ink-2)',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    style={{
                      textDecoration: 'none',
                      color: '#fff',
                      background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
                      padding: '10px 22px',
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 600,
                      boxShadow: '0 4px 14px rgba(59,76,202,.35)',
                    }}
                  >
                    Join Fellowship
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Mobile right side: bell (auth'd) + hamburger */}
          {isMobile && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              {accessToken && (
                <Link
                  to="/profile"
                  aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                  style={{
                    position: 'relative',
                    color: 'var(--ink-2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Bell size={20} strokeWidth={1.7} />
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -4,
                        right: -5,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
                        color: '#fff',
                        fontSize: 9,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              <button
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Menu size={22} strokeWidth={1.8} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile drawer */}
      {isMobile && drawerOpen && (
        <div
          onClick={closeDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            background: 'rgba(15,14,28,0.5)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: 280,
              background: 'var(--bg)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-8px 0 40px rgba(15,14,28,0.18)',
            }}
          >
            {/* Drawer header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--line-soft)' }}>
              <Wordmark size={20} />
              <button
                onClick={closeDrawer}
                aria-label="Close menu"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', display: 'flex', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Nav links */}
            <nav style={{ padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {navlinks.map((item) => {
                const isActive = activeNav === item.link
                return (
                  <NavLink
                    key={item.link}
                    to={item.href}
                    onClick={closeDrawer}
                    style={{
                      textDecoration: 'none',
                      display: 'block',
                      padding: '12px 12px',
                      borderRadius: 10,
                      fontSize: 16,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? 'var(--primary)' : 'var(--ink)',
                      background: isActive ? 'var(--primary-soft)' : 'transparent',
                    }}
                  >
                    {item.link}
                  </NavLink>
                )
              })}
              {accessToken && (
                <NavLink
                  to="/my-events"
                  onClick={closeDrawer}
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    padding: '12px 12px',
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 500,
                    color: 'var(--ink)',
                  }}
                >
                  My Events
                </NavLink>
              )}
            </nav>

            {/* Auth actions */}
            <div style={{ padding: '12px 20px', marginTop: 'auto', borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {accessToken ? (
                <Link
                  to="/profile"
                  onClick={closeDrawer}
                  style={{
                    textDecoration: 'none',
                    textAlign: 'center',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1.5px solid var(--line)',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ink)',
                  }}
                >
                  Profile & settings
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeDrawer}
                    style={{
                      textDecoration: 'none',
                      textAlign: 'center',
                      padding: '12px',
                      borderRadius: 12,
                      border: '1.5px solid var(--line)',
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--ink)',
                    }}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeDrawer}
                    style={{
                      textDecoration: 'none',
                      textAlign: 'center',
                      padding: '13px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      boxShadow: '0 4px 14px rgba(59,76,202,.35)',
                    }}
                  >
                    Join Fellowship
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>{children}</div>

      {/* Footer */}
      {!hideFooter && (
        <footer
          style={{
            borderTop: '1px solid var(--line)',
            padding: isMobile ? '32px 20px 28px' : '48px 36px 40px',
            marginTop: isMobile ? 40 : 80,
          }}
        >
          <div
            style={{
              maxWidth: 1240,
              margin: '0 auto',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: isMobile ? 28 : 40,
            }}
          >
            <div>
              <Wordmark size={20} />
              <p
                style={{
                  marginTop: 12,
                  fontSize: 13.5,
                  color: 'var(--muted)',
                  maxWidth: 280,
                  lineHeight: 1.6,
                }}
              >
                Find and host gatherings with Christians near you — any denomination, any tradition.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 48, fontSize: 13.5 }}>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    marginBottom: 12,
                  }}
                >
                  Discover
                </div>
                {[
                  ['Events', '/search'],
                  ['Churches', '/churches'],
                  ['Host events', '/host'],
                ].map(([label, href]) => (
                  <div key={href} style={{ marginBottom: 8 }}>
                    <Link to={href} style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>
                      {label}
                    </Link>
                  </div>
                ))}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    marginBottom: 12,
                  }}
                >
                  Account
                </div>
                {[
                  ['Sign in', '/login'],
                  ['Register', '/register'],
                  ['Profile', '/profile'],
                ].map(([label, href]) => (
                  <div key={href} style={{ marginBottom: 8 }}>
                    <Link to={href} style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>
                      {label}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              maxWidth: 1240,
              margin: '32px auto 0',
              paddingTop: 20,
              borderTop: '1px solid var(--line-soft)',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: isMobile ? 8 : 0,
              fontSize: 12.5,
              color: 'var(--muted)',
            }}
          >
            <span>© {new Date().getFullYear()} Fellowship</span>
            <div style={{ display: 'flex', gap: 20 }}>
              <Link to="/privacy" style={{ color: 'var(--muted)', textDecoration: 'none' }}>
                Privacy
              </Link>
              <Link to="/terms" style={{ color: 'var(--muted)', textDecoration: 'none' }}>
                Terms
              </Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}
